import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Physics & Engineering — 8 templates
   gear_train, pulley_system, pendulum_swing, magnet_field,
   lens_optics, spring_force, inclined_plane, wave_interference
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Gear Train ──────────────────────────────────────── */
export const GearTrain: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Gear Train";
  const rotation = (props.rotation as number) || 0;
  const accentColor = (props.accentColor as string) || "#ff9800";
  const gearTeeth = (n: number, cx: number, cy: number, r: number, toothH: number) =>
    Array.from({ length: n }, (_, i) => {
      const a = (i * 360 / n) * Math.PI / 180;
      return `${cx + Math.cos(a) * (r + toothH)},${cy + Math.sin(a) * (r + toothH)}
              ${cx + Math.cos(a + 0.5 / n * Math.PI) * r},${cy + Math.sin(a + 0.5 / n * Math.PI) * r}`;
    }).join(" ");
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Three gears in a train */}
      {[
        { cx: 200, cy: 280, r: 80, n: 16, rot: rotation, c: accentColor },
        { cx: 400, cy: 280, r: 60, n: 12, rot: -rotation * 1.33, c: "#3498db" },
        { cx: 550, cy: 280, r: 45, n: 10, rot: rotation * 1.78, c: "#e74c3c" },
      ].map((g, i) => (
        <g key={i} transform={`rotate(${g.rot}, ${g.cx}, ${g.cy})`}>
          <circle cx={g.cx} cy={g.cy} r={g.r} fill="#1c2833" stroke={g.c} strokeWidth="3" />
          <polygon points={gearTeeth(g.n, g.cx, g.cy, g.r - 5, 15)} fill="none" stroke={g.c} strokeWidth="2" />
          <circle cx={g.cx} cy={g.cy} r="10" fill="#0d1117" stroke={g.c} strokeWidth="2" />
        </g>
      ))}
      {/* Speed labels */}
      <text x="200" y="400" textAnchor="middle" fill="#ff9800" fontSize="12">1× speed</text>
      <text x="400" y="380" textAnchor="middle" fill="#3498db" fontSize="12">1.3× speed</text>
      <text x="550" y="365" textAnchor="middle" fill="#e74c3c" fontSize="12">1.8× speed</text>
      <text x="400" y="520" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Pulley System ────────────────────────────────────── */
export const PulleySystem: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Pulley System";
  const animationProgress = (props.animationProgress as number) || 0;
  const lift = animationProgress * 100;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Ceiling */}
      <rect x="100" y="60" width="600" height="15" fill="#546e7a" rx="4" />
      {/* Pulley wheel */}
      <circle cx="400" cy="110" r="30" fill="#1c2833" stroke="#bdbdbd" strokeWidth="4" />
      <circle cx="400" cy="110" r="5" fill="#bdbdbd" />
      {/* Rope - left side (effort) */}
      <line x1="370" y1="110" x2="370" y2={450 - lift * 0.5} stroke="#8d6e63" strokeWidth="3" />
      {/* Rope over pulley */}
      <path d="M370,110 Q370,80 400,80 Q430,80 430,110" fill="none" stroke="#8d6e63" strokeWidth="3" />
      {/* Rope - right side (load) */}
      <line x1="430" y1="110" x2="430" y2={400 - lift} stroke="#8d6e63" strokeWidth="3" />
      {/* Weight */}
      <rect x="400" y={400 - lift} width="60" height="50" rx="6" fill="#e74c3c" opacity="0.8" />
      <text x="430" y={430 - lift} textAnchor="middle" fill="#ecf0f1" fontSize="14" fontWeight="600">W</text>
      {/* Effort arrow */}
      <line x1="340" y1={400 - lift * 0.5} x2="340" y2={450 - lift * 0.5} stroke="#4caf50" strokeWidth="3" markerEnd="url(#pl-arr)" />
      <text x="310" y={430 - lift * 0.5} fill="#4caf50" fontSize="12">F</text>
      <defs>
        <marker id="pl-arr" viewBox="0 0 10 10" refX="5" refY="10" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L5,10 L10,0Z" fill="#4caf50" />
        </marker>
      </defs>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Pendulum Swing ───────────────────────────────────── */
export const PendulumSwing: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Pendulum";
  const phase = (props.phase as number) || 0;
  const angle = Math.sin(phase) * 35;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Support */}
      <rect x="300" y="60" width="200" height="12" rx="4" fill="#546e7a" />
      <g transform={`rotate(${angle}, 400, 72)`}>
        {/* String */}
        <line x1="400" y1="72" x2="400" y2="370" stroke="#8d6e63" strokeWidth="2" />
        {/* Bob */}
        <circle cx="400" cy="390" r="30" fill="#e74c3c" stroke="#c62828" strokeWidth="2" />
      </g>
      {/* Arc path */}
      <path d="M280,390 Q400,420 520,390" fill="none" stroke="#546e7a" strokeWidth="1" strokeDasharray="6,4" />
      {/* Equilibrium line */}
      <line x1="400" y1="72" x2="400" y2="420" stroke="#546e7a" strokeWidth="0.8" strokeDasharray="4,4" />
      {/* Energy labels */}
      <text x="280" y="440" textAnchor="middle" fill="#f9a825" fontSize="12">Max PE</text>
      <text x="400" y="460" textAnchor="middle" fill="#4caf50" fontSize="12">Max KE</text>
      <text x="520" y="440" textAnchor="middle" fill="#f9a825" fontSize="12">Max PE</text>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Magnet Field ─────────────────────────────────────── */
export const MagnetField: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Magnetic Field";
  const flowProgress = (props.flowProgress as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Bar magnet */}
      <rect x="300" y="260" width="100" height="60" rx="6" fill="#e74c3c" />
      <rect x="400" y="260" width="100" height="60" rx="6" fill="#2196f3" />
      <text x="350" y="297" textAnchor="middle" fill="#fff" fontSize="20" fontWeight="700">N</text>
      <text x="450" y="297" textAnchor="middle" fill="#fff" fontSize="20" fontWeight="700">S</text>
      {/* Field lines */}
      {[40, 80, 120].map((offset, i) => (
        <g key={i} opacity={0.4 + i * 0.1}>
          {/* Top field lines */}
          <path d={`M350,260 Q350,${260 - offset} 400,${260 - offset - 20} Q450,${260 - offset} 450,260`}
            fill="none" stroke="#7c4dff" strokeWidth="1.5"
            strokeDasharray="8,4" strokeDashoffset={-flowProgress * 30} />
          {/* Bottom field lines */}
          <path d={`M350,320 Q350,${320 + offset} 400,${320 + offset + 20} Q450,${320 + offset} 450,320`}
            fill="none" stroke="#7c4dff" strokeWidth="1.5"
            strokeDasharray="8,4" strokeDashoffset={-flowProgress * 30} />
          {/* Side field lines */}
          <path d={`M300,290 Q${300 - offset},290 ${300 - offset - 20},${290 - offset * 0.8}
            Q${300 - offset},${290 - offset * 1.6} 350,260`}
            fill="none" stroke="#7c4dff" strokeWidth="1.5"
            strokeDasharray="8,4" strokeDashoffset={-flowProgress * 30} />
          <path d={`M500,290 Q${500 + offset},290 ${500 + offset + 20},${290 - offset * 0.8}
            Q${500 + offset},${290 - offset * 1.6} 450,260`}
            fill="none" stroke="#7c4dff" strokeWidth="1.5"
            strokeDasharray="8,4" strokeDashoffset={-flowProgress * 30} />
        </g>
      ))}
      <text x="400" y="500" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Lens Optics ──────────────────────────────────────── */
export const LensOptics: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Lens & Light";
  const activeIndex = (props.activeIndex as number) ?? -1;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Optical axis */}
      <line x1="60" y1="300" x2="740" y2="300" stroke="#546e7a" strokeWidth="1" strokeDasharray="6,4" />
      {/* Convex lens */}
      <ellipse cx="400" cy="300" rx="15" ry="140" fill="#42a5f5" opacity="0.2" stroke="#42a5f5" strokeWidth="2" />
      {/* Focal points */}
      <circle cx="260" cy="300" r="5" fill="#f9a825" />
      <text x="260" y="325" textAnchor="middle" fill="#f9a825" fontSize="11">F</text>
      <circle cx="540" cy="300" r="5" fill="#f9a825" />
      <text x="540" y="325" textAnchor="middle" fill="#f9a825" fontSize="11">F</text>
      {/* Light rays */}
      <g opacity={activeIndex >= 0 ? 1 : 0.6}>
        {/* Parallel ray → through focal point */}
        <line x1="100" y1="180" x2="400" y2="180" stroke="#f44336" strokeWidth="2" />
        <line x1="400" y1="180" x2="700" y2="380" stroke="#f44336" strokeWidth="2" />
        {/* Through center → straight */}
        <line x1="100" y1="400" x2="700" y2="200" stroke="#4caf50" strokeWidth="2" />
        {/* Through focal point → parallel */}
        <line x1="100" y1="400" x2="400" y2="400" stroke="#2196f3" strokeWidth="2" />
        <line x1="400" y1="400" x2="700" y2="400" stroke="#2196f3" strokeWidth="2" strokeDasharray="6,3" />
      </g>
      {/* Object */}
      <line x1="180" y1="180" x2="180" y2="300" stroke="#ecf0f1" strokeWidth="3" />
      <polygon points="180,180 174,195 186,195" fill="#ecf0f1" />
      <text x="180" y="320" textAnchor="middle" fill="#bdc3c7" fontSize="11">Object</text>
      {/* Image */}
      <line x1="620" y1="300" x2="620" y2="380" stroke="#ecf0f1" strokeWidth="2" strokeDasharray="4,3" />
      <text x="620" y="400" textAnchor="middle" fill="#bdc3c7" fontSize="11">Image</text>
      <text x="400" y="520" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Spring Force ─────────────────────────────────────── */
export const SpringForce: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Spring (Hooke's Law)";
  const phase = (props.phase as number) || 0;
  const stretch = Math.sin(phase) * 60;
  const coils = 8;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Wall */}
      <rect x="100" y="200" width="15" height="160" fill="#546e7a" />
      {Array.from({ length: 6 }, (_, i) => (
        <line key={i} x1="100" y1={210 + i * 28} x2="85" y2={220 + i * 28} stroke="#546e7a" strokeWidth="2" />
      ))}
      {/* Spring coils */}
      <path d={Array.from({ length: coils }, (_, i) => {
        const segW = (280 + stretch) / coils;
        const x = 115 + i * segW;
        return `${i === 0 ? 'M' : 'L'}${x},${260} L${x + segW * 0.25},${240} L${x + segW * 0.75},${320} L${x + segW},${280}`;
      }).join(' ')}
        fill="none" stroke="#4caf50" strokeWidth="3" />
      {/* Mass block */}
      <rect x={395 + stretch} y="250" width="60" height="60" rx="6" fill="#e74c3c" />
      <text x={425 + stretch} y="286" textAnchor="middle" fill="#fff" fontSize="16" fontWeight="600">m</text>
      {/* Force arrow */}
      <line x1={460 + stretch} y1="280" x2={460 + stretch + Math.abs(stretch) * 0.5} y2="280"
        stroke="#f9a825" strokeWidth="3" markerEnd="url(#sf-arr)" />
      <defs>
        <marker id="sf-arr" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10Z" fill="#f9a825" />
        </marker>
      </defs>
      {/* Equation */}
      <text x="400" y="420" textAnchor="middle" fill="#bdc3c7" fontSize="18" fontFamily="serif" fontStyle="italic">F = -kx</text>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Inclined Plane ───────────────────────────────────── */
export const InclinedPlane: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Inclined Plane";
  const animationProgress = (props.animationProgress as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Ground */}
      <line x1="100" y1="460" x2="700" y2="460" stroke="#546e7a" strokeWidth="2" />
      {/* Ramp */}
      <polygon points="150,460 650,460 650,160" fill="#1c2833" stroke="#546e7a" strokeWidth="2" />
      {/* Angle arc */}
      <path d="M200,460 Q200,440 215,430" fill="none" stroke="#f9a825" strokeWidth="2" />
      <text x="230" y="455" fill="#f9a825" fontSize="14">θ</text>
      {/* Block sliding up */}
      {(() => {
        const t = animationProgress;
        const bx = 200 + t * 380;
        const by = 460 - t * 260;
        const angle = -Math.atan2(300, 500) * 180 / Math.PI;
        return (
          <g transform={`translate(${bx},${by}) rotate(${angle})`}>
            <rect x="-25" y="-40" width="50" height="40" rx="4" fill="#e74c3c" />
            <text x="0" y="-16" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="600">m</text>
          </g>
        );
      })()}
      {/* Force arrows */}
      <text x="400" y="510" textAnchor="middle" fill="#bdc3c7" fontSize="13">
        mg sin(θ) = friction + ma
      </text>
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Wave Interference ────────────────────────────────── */
export const WaveInterference: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Wave Interference";
  const phase = (props.phase as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Wave 1 */}
      <path d={`M60,180 ${Array.from({ length: 70 }, (_, i) => {
        const x = 60 + i * 10;
        const y = 180 + Math.sin((i * 0.3) + phase) * 40;
        return `L${x},${y}`;
      }).join(' ')}`} fill="none" stroke="#e74c3c" strokeWidth="2" />
      <text x="40" y="180" fill="#e74c3c" fontSize="12">Wave 1</text>
      {/* Wave 2 */}
      <path d={`M60,340 ${Array.from({ length: 70 }, (_, i) => {
        const x = 60 + i * 10;
        const y = 340 + Math.sin((i * 0.3) + phase + 1) * 40;
        return `L${x},${y}`;
      }).join(' ')}`} fill="none" stroke="#3498db" strokeWidth="2" />
      <text x="40" y="340" fill="#3498db" fontSize="12">Wave 2</text>
      {/* Resultant */}
      <text x="400" y="435" textAnchor="middle" fill="#bdc3c7" fontSize="13">Superposition (Sum)</text>
      <path d={`M60,500 ${Array.from({ length: 70 }, (_, i) => {
        const x = 60 + i * 10;
        const y = 500 + (Math.sin((i * 0.3) + phase) + Math.sin((i * 0.3) + phase + 1)) * 30;
        return `L${x},${y}`;
      }).join(' ')}`} fill="none" stroke="#2ecc71" strokeWidth="2.5" />
      {/* Plus sign */}
      <text x="400" y="270" textAnchor="middle" fill="#ecf0f1" fontSize="30" fontWeight="300">+</text>
      {/* Equals sign */}
      <text x="400" y="410" textAnchor="middle" fill="#ecf0f1" fontSize="30" fontWeight="300">=</text>
      <text x="400" y="575" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
